import React, { createContext, useContext, useEffect, useState } from "react";
export type UserRole = "guest" | "user";

/** Subscription info attached to the logged-in user (API / localStorage). */
export interface UserSubscription {
  tier?: "free" | "personal" | "family" | "professional" | string;
}

export interface User {
  id: string;
  _id?: string;
  email: string;
  name: string;
  full_name?: string;
  first_name?: string;
  firstName?: string;
  last_name?: string;
  lastName?: string;
  phone_number?: string;
  phone?: string;
  bio?: string;
  profile_picture?: string;
  image?: string;
  /** From API / Google redirect; false until create-profile is submitted. */
  isProfileComplete?: boolean;
  role?: string;
  userType?: UserRole;
  subscription?: UserSubscription;
}

interface AuthContextType {
  user: User | null;
  token: string | null;
  role: UserRole;
  isAuthenticated: boolean;
  loading: boolean;
  loginSession: (user: User, token: string) => void;
  signup: (email: string, password: string, name: string) => Promise<boolean>;
  logout: () => void;
  updateUser: (updates: Partial<User>) => void; 
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used within an AuthProvider");
  return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [role, setRole] = useState<UserRole>("guest");
  const [loading, setLoading] = useState(true);

 useEffect(() => {
  const storedUser = localStorage.getItem("user");
  const storedToken = localStorage.getItem("token");

  if (storedUser && storedUser !== "undefined" && storedToken) {
    try {
      const parsedUser = JSON.parse(storedUser);
      setUser(parsedUser);
      setToken(storedToken);
      setRole((parsedUser?.userType || "guest") as UserRole);
    } catch (err) {
      console.error("Invalid user data in localStorage:", err);
      localStorage.removeItem("user");
      localStorage.removeItem("token");
    }
  }

  setLoading(false);
}, []);
  const loginSession = (user: User, token: string) => {
    setUser(user);
    setToken(token);
    setRole(user?.userType ?? "user");
    localStorage.setItem("user", JSON.stringify(user));
    localStorage.setItem("token", token);
  };

  const signup = async (
    email: string,
    password: string,
    name: string
  ): Promise<boolean> => {
    const storedUsers = JSON.parse(
      localStorage.getItem("neighborly_users") || "[]"
    );
    if (storedUsers.find((u) => u?.email === email)) return false;

    const newUser = {
      id: Date.now().toString(),
      email,
      password,
      name,
      userType: "USER" as UserRole,
    };
    storedUsers.push(newUser);
    localStorage.setItem("Connect_users", JSON.stringify(storedUsers));

    const { password: _, ...userData } = newUser;
    setUser(userData);
    setRole("user");
    localStorage.setItem("user", JSON.stringify(userData));
    return true;
  };

  const logout = () => {
    setUser(null);
    setToken(null);
    setRole("guest");
    localStorage.removeItem("user");
    localStorage.removeItem("token");
    window.location.href = "/";
  };

  const updateUser = (updates: Partial<User>) => {
    if (!user) return;
    const updatedUser = { ...user, ...updates };
    setUser(updatedUser);
    localStorage.setItem("user", JSON.stringify(updatedUser));
  };

  const value: AuthContextType = {
    user,
    token,
    role,
    isAuthenticated: !!user,
    loading,
    loginSession,
    signup,
    logout,
    updateUser, 
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
